/**
 * Pure utility functions for PricingCalculatorTab eligibility logic.
 * Extracted for testability — no React or Cloudscape dependencies.
 */

/**
 * Detect a 3-year commitment term. Mirrors the backend canonical
 * is_three_year_term() (ChronosLambdas shared/pricing_terms.py) and the copy in
 * rubiconServiceCategories.js so MAP, Rubicon, and the backend amortize upfront
 * costs identically. Matches "3yr"/"3 year"/"36 months" forms; deliberately
 * does NOT match a bare "3"/"36" or "3 months".
 *
 * @param {string} term - Raw pricing_strategy_term from the calculator item.
 * @returns {boolean} True when the term is a 3-year commitment.
 */
export const isThreeYearTerm = (term) => {
  if (!term) return false;
  const t = String(term).toLowerCase().trim();
  return (
    ['3yr', '3 yr', '3year', '3 year'].includes(t) ||
    /\b3\s*(yr|year)s?\b/.test(t) ||
    /\b36\s*(month|mo)s?\b/.test(t)
  );
};

/**
 * Compute the 3-year upfront amortization breakdown for display transparency
 * (P508436298). A 3-year upfront covers 36 months, so only 1/3 counts toward
 * the annual steady-state ARR used for funding; the excess 2/3 is deducted.
 * This surfaces all three figures so the user can see how the deducted amount
 * was derived.
 *
 * Only positive upfront on a 3-year term is amortized — the same guard the
 * backend amortize_upfront() applies (1-year/unknown terms and non-positive
 * upfront contribute nothing), so a 1-year-only estimate yields a zero
 * deduction and the caller hides the amortization display entirely.
 *
 * The three figures reconcile exactly by construction:
 *   totalUpfront = perYearPortion + deduction
 *
 * @param {Array} services - Raw services[] from the calculator payload.
 * @param {(service: object) => boolean} [includeService] - Optional predicate to
 *   restrict which services are counted. Defaults to including all. The Rubicon
 *   tab passes a predicate that excludes AWS Support so the breakdown reconciles
 *   with its Support-excluded ARR chain (rawARR - supportRawARR - deduction ===
 *   totalARR).
 * @returns {{totalUpfront: number, perYearPortion: number, deduction: number}}
 */
export const computeAmortizationBreakdown = (services, includeService = () => true) => {
  let totalUpfront = 0;
  let deduction = 0;
  for (const service of services || []) {
    if (!includeService(service)) continue;
    const upfront = service?.upfront_cost || 0;
    if (upfront > 0 && isThreeYearTerm(service?.pricing_strategy_term)) {
      totalUpfront += upfront;
      deduction += upfront - upfront / 3;  // 2/3 of upfront is deducted
    }
  }
  return { totalUpfront, perYearPortion: totalUpfront - deduction, deduction };
};

/**
 * Resolve the modernization pathway name for a service_code from the backend
 * modernizationPathways payload.
 *
 * The pathway data keys services on the base AWS service code. The
 * non-credit-eligible half of a QuickSight split carries the Chronos-internal
 * "__pro_spice" suffix, which the backend strips before pathway classification
 * (shared/modernization.py). Strip it here too so the raw suffixed code still
 * matches its pathway — otherwise QuickSight Pro/SPICE falls through to
 * "Non Modern" for this per-service label, contradicting the Modernization
 * Index / ARR, which correctly count it under "Move to Managed Analytics".
 *
 * @param {string} serviceCode - Raw service_code from the calculator item.
 * @param {object} modernizationPathways - Backend modernizationPathways payload.
 * @returns {string} Pathway name, or 'Non Modern' when unmatched/unavailable.
 */
export const getServicePathway = (serviceCode, modernizationPathways) => {
  if (!modernizationPathways?.pathways) return 'Non Modern';

  const lookupCode = (serviceCode || '').replace(/__pro_spice$/, '');

  for (const pathway of modernizationPathways.pathways) {
    const service = pathway.services?.find(s => s.serviceCode === lookupCode);
    if (service) {
      return pathway.name;
    }
  }
  return 'Non Modern';
};

/**
 * Classify qualified services into credit-eligible vs ARR-only buckets.
 * Used by renderEligibleServices().
 */
export const classifyEligibleServices = (services, eligibilityMap) => {
  const creditEligible = [];
  const arrOnly = [];

  for (const service of services) {
    if (!eligibilityMap) {
      creditEligible.push(service);
    } else {
      const tier = eligibilityMap.get(service.service_code)?.tier;
      if (tier === 'credit_eligible') {
        creditEligible.push(service);
      } else {
        arrOnly.push(service);
      }
    }
  }
  return { creditEligible, arrOnly };
};

/**
 * Compute MAP credit-eligible ARR. Uses backend value when available,
 * otherwise filters by tier === 'credit_eligible'.
 */
export const computeCreditEligibleARR = (calculatorDetails, services, eligibilityMap) => {
  const backendValue = calculatorDetails?.fundingEligibility?.awsCredits?.migrateAndModernize?.eligibleARR;
  if (backendValue != null) return backendValue;

  return services
    .filter(service => {
      if ((service.map_qualified_mrr || 0) <= 0) return false;
      if (!eligibilityMap) return true;
      return eligibilityMap.get(service.service_code)?.tier === 'credit_eligible';
    })
    .reduce((sum, service) => sum + (((service.monthly_cost || 0) * 12) + (service.upfront_cost || 0)), 0);
};
